import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Person,
  Security,
  Delete,
  Refresh,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { format } from 'date-fns';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>{children}</Box>}
  </div>
);

const Settings = () => {
  const { admin, updateProfile } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [profileData, setProfileData] = useState({
    email: admin?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const { data: sessionsData, isLoading: sessionsLoading, refetch: refetchSessions } = useQuery(
    'admin-sessions',
    () => authAPI.getSessions(),
    {
      refetchInterval: 90 * 1000, // Refetch every 90 seconds
    }
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user starts typing
    if (profileErrors[name]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Clear success message
    if (profileSuccess) {
      setProfileSuccess('');
    }
  };

  const validateProfileForm = () => {
    const errors = {};
    
    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (profileData.newPassword) {
      if (!profileData.currentPassword) {
        errors.currentPassword = 'Current password is required to change password';
      }
      
      if (profileData.newPassword.length < 8) {
        errors.newPassword = 'New password must be at least 8 characters';
      }
      
      if (profileData.newPassword !== profileData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    setProfileLoading(true);
    
    const updateData = {
      email: profileData.email,
    };
    
    if (profileData.newPassword) {
      updateData.currentPassword = profileData.currentPassword;
      updateData.newPassword = profileData.newPassword;
    }
    
    const result = await updateProfile(updateData);
    
    if (result.success) {
      setProfileSuccess('Profile updated successfully');
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    }
    
    setProfileLoading(false);
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await authAPI.revokeSession(sessionId);
      refetchSessions();
    } catch (error) {
      console.error('Failed to revoke session:', error);
    }
  };

  const sessions = sessionsData?.data?.sessions || [];

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<Person />} label="Profile" />
          <Tab icon={<Security />} label="Security" />
        </Tabs>
      </Box>

      {/* Profile Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profile Information
                </Typography>
                
                {profileSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {profileSuccess}
                  </Alert>
                )}
                
                <Box component="form" onSubmit={handleProfileSubmit} sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={admin?.username || ''}
                        disabled
                        helperText="Username cannot be changed"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Role"
                        value={admin?.role || ''}
                        disabled
                        helperText="Role is assigned by system administrator"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={profileData.email}
                        onChange={handleProfileChange}
                        error={!!profileErrors.email}
                        helperText={profileErrors.email}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Change Password
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        name="currentPassword"
                        type="password"
                        value={profileData.currentPassword}
                        onChange={handleProfileChange}
                        error={!!profileErrors.currentPassword}
                        helperText={profileErrors.currentPassword}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        name="newPassword"
                        type="password"
                        value={profileData.newPassword}
                        onChange={handleProfileChange}
                        error={!!profileErrors.newPassword}
                        helperText={profileErrors.newPassword}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        name="confirmPassword"
                        type="password"
                        value={profileData.confirmPassword}
                        onChange={handleProfileChange}
                        error={!!profileErrors.confirmPassword}
                        helperText={profileErrors.confirmPassword}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={profileLoading}
                        sx={{ mt: 2 }}
                      >
                        {profileLoading ? 'Updating...' : 'Update Profile'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {admin?.lastLogin ? format(new Date(admin.lastLogin), 'PPpp') : 'Never'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Permissions
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {admin?.permissions?.map((permission) => (
                      <Chip
                        key={permission}
                        label={permission}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Active Sessions
                  </Typography>
                  <IconButton onClick={refetchSessions} disabled={sessionsLoading}>
                    <Refresh />
                  </IconButton>
                </Box>
                
                {sessionsLoading ? (
                  <LoadingSpinner message="Loading sessions..." />
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Session</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Last Activity</TableCell>
                          <TableCell>IP Address</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sessions.map((session) => (
                          <TableRow key={session.sessionId}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {session.sessionId.substring(0, 8)}...
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {format(new Date(session.createdAt), 'MMM dd, HH:mm')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(session.lastActivity), 'MMM dd, HH:mm')}
                            </TableCell>
                            <TableCell>{session.ipAddress}</TableCell>
                            <TableCell>
                              <Chip
                                label={session.isCurrent ? 'Current' : 'Active'}
                                color={session.isCurrent ? 'primary' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {!session.isCurrent && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRevokeSession(session.sessionId)}
                                >
                                  <Delete />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default Settings;
